/* ShelfKnight theme behaviour: the 3D books (loading and the slow turn),
   the AJAX cart drawer, search with suggestions, the sticky Add to cart on
   phones, the reader dialog and the read-next fetch. No framework. */
(function () {
  'use strict';

  var SK = window.SK || { routes: {}, strings: {} };
  var reduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* Keyboard modality: the 3D book takes focus inside its own shadow root, so
     its ring is shown from this class instead of :focus-visible. */
  var root = document.documentElement;
  document.addEventListener('keydown', function (e) { if (e.key === 'Tab') root.classList.add('sk-kb'); }, true);
  document.addEventListener('pointerdown', function () { root.classList.remove('sk-kb'); }, true);

  /* ---------- model-viewer, loaded once and only when needed ---------- */
  var mvPromise = null;
  function loadModelViewer() {
    if (window.customElements && customElements.get('model-viewer')) return Promise.resolve();
    if (mvPromise) return mvPromise;
    mvPromise = new Promise(function (resolve) {
      /* Shopify's own loader first (the same model-viewer the platform ships
         for 3D media); Google's CDN build if the platform loader is absent. */
      function viaCdn() {
        if (customElements.get('model-viewer')) return resolve();
        var s = document.createElement('script');
        s.type = 'module';
        s.src = 'https://ajax.googleapis.com/ajax/libs/model-viewer/3.5.0/model-viewer.min.js';
        s.onload = function () { resolve(); };
        s.onerror = function () { resolve(); };
        document.head.appendChild(s);
      }
      if (window.Shopify && typeof window.Shopify.loadFeatures === 'function') {
        window.Shopify.loadFeatures([{ name: 'model-viewer-ui', version: '1.0', onLoad: function (err) { if (err || !customElements.get('model-viewer')) viaCdn(); else resolve(); } }]);
        setTimeout(function () { if (!customElements.get('model-viewer')) viaCdn(); }, 2500);
      } else {
        viaCdn();
      }
    });
    return mvPromise;
  }

  /* ---------- the turn ----------
     The GLBs are bound with a 15deg yaw baked in, so the front board faces
     the camera at theta 15deg. "spin" goes all the way round but lingers on
     the boards and hurries past the edges, so a thin book never sits
     spine-on for long; "sway" rocks the product page around its
     three-quarter view. Both read the orbit the visitor left, and wait. */
  var turns = [];
  var COVER = 15;
  function Turn(mv) {
    var o = (mv.getAttribute('camera-orbit') || '0deg 80deg 92%').split(/\s+/);
    this.mv = mv;
    this.mode = mv.getAttribute('data-sk-turn') || 'spin';
    this.theta = parseFloat(o[0]) || 0;
    this.rest = this.theta;
    this.phi = o[1] || '80deg';
    this.radius = o[2] || '92%';
    /* On a phone the camera stands back a little, so the book clears the gauge. */
    if (window.matchMedia('(max-width: 40rem)').matches) this.radius = Math.round(parseFloat(this.radius) * (this.mode === 'sway' ? 1.16 : 1.08)) + '%';
    mv.cameraOrbit = this.theta + 'deg ' + this.phi + ' ' + this.radius;
    this.t = 0;
    this.visible = true;
    this.holdUntil = this.mode === 'sway' ? performance.now() + 2500 : 0;
    var self = this;
    mv.addEventListener('camera-change', function (e) {
      if (e.detail && e.detail.source === 'user-interaction') {
        self.holdUntil = performance.now() + 3500;
        var orbit = mv.getCameraOrbit && mv.getCameraOrbit();
        if (orbit) { self.theta = orbit.theta * 180 / Math.PI; self.phi = (orbit.phi * 180 / Math.PI) + 'deg'; }
        self.t = 0;
      }
    });
    if ('IntersectionObserver' in window) {
      new IntersectionObserver(function (es) { self.visible = es[0].isIntersecting; }).observe(mv);
    }
  }
  Turn.prototype.step = function (now, dt) {
    if (!this.visible || now < this.holdUntil) return;
    if (this.mode === 'spin') {
      var off = (this.theta - COVER) * Math.PI / 180;
      /* 7deg/s on a board, about 34deg/s edge-on */
      var speed = 7 + 27 * Math.pow(Math.abs(Math.sin(off)), 1.6);
      this.theta += speed * dt;
      if (this.theta > 360 + COVER) this.theta -= 360;
    } else {
      this.t += dt;
      var target = this.rest + 20 * Math.sin(this.t * 2 * Math.PI / 16);
      /* ease back toward the sway after a hand has moved it */
      this.theta += (target - this.theta) * Math.min(1, dt * 1.4);
    }
    this.mv.cameraOrbit = this.theta.toFixed(2) + 'deg ' + this.phi + ' ' + this.radius;
  };
  var lastFrame = 0, rafId = 0;
  function frame(now) {
    var dt = lastFrame ? Math.min(0.05, (now - lastFrame) / 1000) : 0;
    lastFrame = now;
    turns.forEach(function (t) { t.step(now, dt); });
    rafId = requestAnimationFrame(frame);
  }
  function runTurns() {
    var on = turns.length && !reduce.matches && !document.hidden;
    if (on && !rafId) { lastFrame = 0; rafId = requestAnimationFrame(frame); }
    if (!on && rafId) { cancelAnimationFrame(rafId); rafId = 0; }
  }
  document.addEventListener('visibilitychange', runTurns);
  if (reduce.addEventListener) reduce.addEventListener('change', runTurns);

  function dressModel(mv) {
    mv.addEventListener('load', function () {
      var stage = mv.closest('[data-sk-stage]');
      if (stage) stage.classList.add('is-live');
      if (mv.hasAttribute('data-sk-turn') && !mv.__skTurn) {
        mv.__skTurn = new Turn(mv);
        turns.push(mv.__skTurn);
        runTurns();
      }
    });
    mv.addEventListener('error', function () {
      var stage = mv.closest('[data-sk-stage]');
      if (stage) stage.classList.add('is-failed');
    });
  }

  function initStages(root) {
    var stages = (root || document).querySelectorAll('[data-sk-stage]');
    var lazy = [];
    var eager = false;
    Array.prototype.forEach.call(stages, function (stage) {
      var mv = stage.querySelector('model-viewer');
      if (!mv) return;
      if (stage.__skInit) return;
      stage.__skInit = true;
      dressModel(mv);
      if (stage.hasAttribute('data-eager')) eager = true;
      else lazy.push(stage);
    });
    /* Above the fold the model's own preview render is already on screen
       (it is the LCP image), so the 3D library waits. On a wide screen it
       loads once the page has loaded and the main thread is quiet; on a
       phone, at the first touch, scroll or key press, so a visitor who only
       reads never pays for it. A hand on the stage loads it at once. */
    if (eager) {
      var go = function () { loadModelViewer(); };
      var small = window.matchMedia('(max-width: 61.9rem), (pointer: coarse)').matches;
      if (small) {
        var first = function () {
          ['pointerdown', 'scroll', 'keydown', 'touchstart'].forEach(function (ev) { window.removeEventListener(ev, first, true); });
          go();
        };
        ['pointerdown', 'scroll', 'keydown', 'touchstart'].forEach(function (ev) { window.addEventListener(ev, first, { capture: true, passive: true }); });
      } else {
        var idle = window.requestIdleCallback || function (cb) { return setTimeout(cb, 300); };
        var whenLoaded = function () { setTimeout(function () { idle(go, { timeout: 2500 }); }, 600); };
        if (document.readyState === 'complete') whenLoaded(); else window.addEventListener('load', whenLoaded, { once: true });
      }
      Array.prototype.forEach.call(stages, function (st) {
        if (!st.hasAttribute('data-eager')) return;
        ['pointerdown', 'focusin'].forEach(function (ev) { st.addEventListener(ev, go, { once: true, passive: true }); });
      });
    }
    if (!lazy.length) return;
    if (!('IntersectionObserver' in window)) { loadModelViewer(); return; }
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) { loadModelViewer(); io.unobserve(e.target); }
      });
    }, { rootMargin: '400px 0px' });
    lazy.forEach(function (s) { io.observe(s); });
  }

  /* ---------- cart ---------- */
  var drawer = function () { return document.querySelector('[data-sk-drawer]'); };

  function updateCount(n) {
    document.querySelectorAll('[data-sk-cart-count]').forEach(function (el) {
      var label = el.querySelector('.visually-hidden');
      el.textContent = '';
      if (label) el.appendChild(label);
      el.appendChild(document.createTextNode(String(n)));
    });
  }

  function swapDrawer(html) {
    if (!html) return;
    var doc = new DOMParser().parseFromString(html, 'text/html');
    var fresh = doc.querySelector('[data-sk-drawer-body]');
    var body = document.querySelector('[data-sk-drawer-body]');
    if (fresh && body) {
      body.innerHTML = fresh.innerHTML;
      body.setAttribute('data-count', fresh.getAttribute('data-count'));
      updateCount(parseInt(fresh.getAttribute('data-count'), 10) || 0);
    }
  }

  function openDrawer() {
    var d = drawer();
    if (!d) { window.location.href = SK.routes.cart; return; }
    if (typeof d.showModal !== 'function') { window.location.href = SK.routes.cart; return; }
    if (!d.open) d.showModal();
    document.documentElement.classList.add('sk-locked');
    var first = d.querySelector('[data-sk-cart-close]');
    if (first) first.focus();
  }
  function closeDrawer() {
    var d = drawer();
    if (d && d.open) d.close();
  }
  document.addEventListener('close', function (e) {
    if (e.target.matches && e.target.matches('dialog')) document.documentElement.classList.remove('sk-locked');
  }, true);

  document.addEventListener('click', function (e) {
    var open = e.target.closest('[data-sk-cart-open]');
    if (open && drawer()) { e.preventDefault(); openDrawer(); return; }
    if (e.target.closest('[data-sk-cart-close]')) { closeDrawer(); return; }
    var d = drawer();
    if (d && e.target === d) closeDrawer();
  });

  function postJSON(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    }).then(function (r) { return r.json().then(function (j) { return { ok: r.ok, json: j }; }); });
  }

  document.addEventListener('submit', function (e) {
    var form = e.target.closest('[data-sk-product-form]');
    if (!form || !drawer() || !window.fetch) return;
    e.preventDefault();
    var quiet = form.hasAttribute('data-sk-quiet');
    var btn = form.querySelector('[type="submit"]');
    var status = form.querySelector('.sk-buy__status');
    var id = form.querySelector('[name="id"]').value;
    if (btn) { btn.setAttribute('aria-busy', 'true'); btn.disabled = true; }
    if (status) status.textContent = '';
    postJSON(SK.routes.cartAdd + '.js', {
      items: [{ id: Number(id), quantity: 1 }],
      sections: 'sk-cart-drawer',
      sections_url: window.location.pathname
    }).then(function (res) {
      if (!res.ok) throw new Error(res.json && (res.json.description || res.json.message) || SK.strings.error);
      if (quiet && document.body.classList.contains('sk-template-cart')) { window.location.reload(); return; }
      var d = drawer();
      var inDrawer = d && d.contains(form);
      if (res.json.sections) swapDrawer(res.json.sections['sk-cart-drawer']);
      if (status) status.textContent = SK.strings.added;
      if (!quiet) { openDrawer(); return; }
      /* Add-ons stay put: the button says so, a View cart link appears. */
      if (inDrawer) { var c = d.querySelector('[data-sk-cart-close]'); if (c) c.focus(); return; }
      if (btn && btn.getAttribute('data-sk-added-label')) { btn.textContent = btn.getAttribute('data-sk-added-label'); btn.classList.add('is-added'); }
      var view = form.parentNode && form.parentNode.querySelector('[data-sk-addon-view]');
      if (view) view.hidden = false;
    }).catch(function (err) {
      if (status) status.textContent = err && err.message ? err.message : SK.strings.error;
    }).then(function () {
      if (btn) { btn.removeAttribute('aria-busy'); btn.disabled = false; }
    });
  });

  function changeLine(key, qty, line) {
    if (line) line.setAttribute('aria-busy', 'true');
    return postJSON(SK.routes.cartChange + '.js', {
      id: key, quantity: qty, sections: 'sk-cart-drawer', sections_url: window.location.pathname
    }).then(function (res) {
      if (!res.ok) throw new Error(res.json && res.json.description || SK.strings.error);
      if (document.body.classList.contains('sk-template-cart')) { window.location.reload(); return; }
      if (res.json.sections) swapDrawer(res.json.sections['sk-cart-drawer']);
      else updateCount(res.json.item_count);
    }).catch(function () {
      if (line) line.removeAttribute('aria-busy');
    });
  }

  document.addEventListener('click', function (e) {
    var b = e.target.closest('[data-sk-qty]');
    if (!b) return;
    var line = b.closest('.sk-line');
    var input = line && line.querySelector('[data-sk-qty-input]');
    if (!input) return;
    var next = Math.max(0, (parseInt(input.value, 10) || 0) + parseInt(b.getAttribute('data-sk-qty'), 10));
    input.value = next;
    changeLine(line.getAttribute('data-key'), next, line);
  });
  document.addEventListener('change', function (e) {
    var input = e.target.closest('[data-sk-qty-input]');
    if (input) {
      var line = input.closest('.sk-line');
      changeLine(line.getAttribute('data-key'), Math.max(0, parseInt(input.value, 10) || 0), line);
      return;
    }
    var sel = e.target.closest('[data-sk-variant-select]');
    if (sel) {
      var opt = sel.options[sel.selectedIndex];
      var scope = sel.closest('.sk-pdp__panel');
      var price = scope && scope.querySelector('.sk-price');
      if (price && opt && opt.getAttribute('data-price')) price.textContent = opt.getAttribute('data-price');
    }
  });

  /* ---------- reader dialog ---------- */
  document.addEventListener('click', function (e) {
    var o = e.target.closest('[data-sk-dialog-open]');
    if (o) {
      var dlg = document.getElementById(o.getAttribute('data-sk-dialog-open'));
      if (dlg && typeof dlg.showModal === 'function') {
        dlg.showModal();
        document.documentElement.classList.add('sk-locked');
        var page = dlg.querySelector('.sk-reader__page');
        if (page) page.focus();
      }
      return;
    }
    if (e.target.closest('[data-sk-dialog-close]')) {
      var d = e.target.closest('dialog');
      if (d) d.close();
      return;
    }
    if (e.target.matches && e.target.matches('dialog.sk-reader')) e.target.close();
  });

  /* ---------- read next ---------- */
  function initRecs() {
    document.querySelectorAll('[data-sk-recs]').forEach(function (sec) {
      if (sec.__skRecs || sec.children.length) return;
      sec.__skRecs = true;
      var go = function () {
        fetch(sec.getAttribute('data-url')).then(function (r) { return r.text(); }).then(function (html) {
          var doc = new DOMParser().parseFromString(html, 'text/html');
          var fresh = doc.querySelector('[data-sk-recs]');
          if (fresh && fresh.innerHTML.trim()) {
            sec.innerHTML = fresh.innerHTML;
          }
        }).catch(function () {});
      };
      if ('IntersectionObserver' in window) {
        var io = new IntersectionObserver(function (es) {
          if (es[0].isIntersecting) { io.disconnect(); go(); }
        }, { rootMargin: '600px 0px' });
        io.observe(sec);
      } else { go(); }
    });
  }


  /* ---------- search ----------
     The header's Search link opens a dialog. Typing asks Shopify's
     predictive search (/search/suggest.json) for books and pages, and
     matches authors from the list the layout prints (author pages are
     metaobjects, which predictive search does not cover). Arrow keys move
     through the list; Enter opens the active entry or the full results. */
  function initFind() {
    var dlg = document.getElementById('SkFind');
    if (!dlg || typeof dlg.showModal !== 'function') return;
    var input = dlg.querySelector('[data-sk-find-input]');
    var list = dlg.querySelector('[data-sk-find-list]');
    var status = dlg.querySelector('[data-sk-find-status]');
    var active = -1, timer = 0, seq = 0;
    function esc(t) { var d = document.createElement('div'); d.textContent = t == null ? '' : String(t); return d.innerHTML; }
    function items() { return list.querySelectorAll('[role="option"]'); }
    function setActive(i) {
      var opts = items();
      if (!opts.length) { active = -1; input.removeAttribute('aria-activedescendant'); return; }
      active = (i + opts.length) % opts.length;
      opts.forEach(function (o, k) { o.setAttribute('aria-selected', k === active ? 'true' : 'false'); });
      input.setAttribute('aria-activedescendant', opts[active].id);
      opts[active].scrollIntoView({ block: 'nearest' });
    }
    function render(q, books, pages, authors) {
      var html = '', n = 0;
      function row(href, main, side, kind) {
        n++;
        html += '<li role="option" id="SkFindOpt' + n + '" aria-selected="false" class="sk-find__opt"><a href="' + esc(href) + '" tabindex="-1"><span class="sk-find__kind">' + esc(kind) + '</span><span class="sk-find__main">' + esc(main) + '</span><span class="sk-find__side">' + esc(side || '') + '</span></a></li>';
      }
      authors.forEach(function (a) { row(a.url, a.name, a.life, SK.strings.authors); });
      books.forEach(function (b) { row(b.url, (b.title || '').split(' by ')[0], b.price ? b.price : '', b.mark ? (SK.strings.bookmark || SK.strings.books) : SK.strings.books); });
      pages.forEach(function (p) { row(p.url, p.title, '', ''); });
      var all = SK.routes.search + '?type=product,page&q=' + encodeURIComponent(q);
      n++;
      html += '<li role="option" id="SkFindOpt' + n + '" aria-selected="false" class="sk-find__opt sk-find__opt--all"><a href="' + esc(all) + '" tabindex="-1">' + esc((n > 1 ? SK.strings.viewAll : SK.strings.none).replace('__T__', q)) + '</a></li>';
      list.innerHTML = html;
      input.setAttribute('aria-expanded', 'true');
      active = -1;
      if (status) status.textContent = (n - 1) + ' ' + (SK.strings.count || 'suggestions');
    }
    function money(cents) {
      try { return new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: (window.Shopify && Shopify.currency && Shopify.currency.active) || 'USD' }).format(cents); } catch (e) { return ''; }
    }
    function suggest() {
      var q = input.value.trim();
      if (!q) { list.innerHTML = ''; input.setAttribute('aria-expanded', 'false'); return; }
      var my = ++seq;
      var ql = q.toLowerCase();
      var authors = (SK.authors || []).filter(function (a) { return a.name.toLowerCase().indexOf(ql) !== -1; }).slice(0, 3);
      var url = SK.routes.suggest + '?q=' + encodeURIComponent(q) + '&resources[type]=product,page&resources[limit]=6&resources[options][unavailable_products]=last';
      fetch(url, { headers: { Accept: 'application/json' } }).then(function (r) { return r.json(); }).then(function (j) {
        if (my !== seq) return;
        var res = (j && j.resources && j.resources.results) || {};
        var books = (res.products || []).map(function (p) { return { url: p.url, title: p.title, price: p.price ? money(p.price) : '', mark: p.type === 'Bookmark' }; });
        /* Predictive search also matches body text, so a query for an author
           can surface every essay that mentions them. Titles and authors that
           match outright win; the loose matches show only when nothing does. */
        var words = ql.split(/\s+/);
        var direct = books.filter(function (b) { var t = (b.title || '').toLowerCase(); return words.every(function (w) { return t.indexOf(w) !== -1; }); });
        if (direct.length) books = direct;
        render(q, books, res.pages || [], authors);
      }).catch(function () { if (my === seq) render(q, [], [], authors); });
    }
    input.addEventListener('input', function () { clearTimeout(timer); timer = setTimeout(suggest, 140); });
    input.addEventListener('keydown', function (e) {
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
      else if (e.key === 'Enter' && active >= 0) {
        var a = items()[active].querySelector('a');
        if (a) { e.preventDefault(); window.location.href = a.href; }
      }
    });
    document.addEventListener('click', function (e) {
      var o = e.target.closest('[data-sk-search-open]');
      if (o) {
        e.preventDefault();
        dlg.showModal();
        document.documentElement.classList.add('sk-locked');
        input.focus();
        input.select();
        return;
      }
      if (e.target.closest('[data-sk-find-close]') || e.target === dlg) dlg.close();
    });
  }

  /* ---------- sticky Add to cart (phones) ----------
     Shown once the page's own price and button have scrolled up out of
     view; hidden again near them. The bar's button submits the same form. */
  function initSticky() {
    var bar = document.querySelector('[data-sk-sticky]');
    var main = document.querySelector('[data-sk-buy-main]');
    if (!bar || !main || !('IntersectionObserver' in window)) return;
    new IntersectionObserver(function (es) {
      var e = es[0];
      var past = !e.isIntersecting && e.boundingClientRect.top < 0;
      bar.hidden = !past;
      document.body.classList.toggle('has-sticky', past);
    }).observe(main);
  }

  function boot() {
    initStages();
    initRecs();
    initFind();
    initSticky();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
  document.addEventListener('shopify:section:load', function (e) { initStages(e.target); initRecs(); });
})();
